/* C3 — Celulares conectados (Painel do Cliente).
 *
 * A empresa vê os próprios números de WhatsApp e conecta novos — em linguagem
 * de usuário final, sem servidor, chave ou proxy à vista. Desenhada na variante
 * clara do Nebula (o design system compartilhado, escopo `.theme-light`).
 *
 * Ligada ao real: a lista vem de `/api/numbers`, e o fluxo de conectar é o de
 * verdade (Fase D): cria o cadastro (`POST /api/numbers`), cria a instância na
 * Evolution e mostra o QR (`POST /api/numbers/<id>/instance`), e sonda o
 * pareamento (`GET .../instance/estado`) até abrir.
 *
 * DUAS regras da sondagem, que já custaram uma tela travada para sempre:
 *   1. Quem se pergunta "já pareou?" é `/estado` — leitura barata. NUNCA o
 *      `/qrcode`: ele manda a Evolution cunhar um código novo, e pedir um a
 *      cada volta troca o QR debaixo de quem está mirando a câmera.
 *   2. Quando abre, a resposta diz QUEM entrou (`dono`). É isso que o backend
 *      grava como JID do número — sem esse registro o número ficaria mudo. Por
 *      isso a tela mostra o telefone confirmado, e não só "conectado". */

import React, { useEffect, useRef, useState } from 'react';

import { api } from '../api/cliente';
import { relativo, telefoneLegivel } from '../admin/formato';
import { Aviso, Button, card, Cabecalho, Icon, Nota, Tag } from '../admin/ui';
import { useRecurso } from '../estado/recurso';
import { useSessao } from '../estado/sessao';

const INTERVALO_DE_SONDAGEM_MS = 2500;

function estado(e) {
  if (e === 'open') return { tone: 'success', rotulo: 'Conectado', ok: true };
  if (e === 'connecting') return { tone: 'warning', rotulo: 'Conectando', ok: false };
  return { tone: 'danger', rotulo: 'Desconectado', ok: false };
}

const botaoPequeno = { height: 32, padding: '0 10px', fontSize: 'var(--fs-label)' };

export default function Celulares() {
  const { tenants } = useSessao();
  const recurso = useRecurso((sinal) => api.numeros(sinal), []);
  const numeros = recurso.dados?.itens || [];
  // `null` = fechado; `{ novo: true }` = conectar novo; `{ numero }` = reconectar.
  const [conexao, definirConexao] = useState(null);

  const empresa = tenants[0]?.sender || null;

  function fechar(mudou) {
    definirConexao(null);
    if (mudou) recurso.recarregar();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Cabecalho
        titulo="Celulares conectados"
        descricao="Os números de WhatsApp que atendem pela Sofia. Conecte novos quando precisar."
        acoes={<Button variant="primary" icon="plus" onClick={() => definirConexao({ novo: true })} disabled={!empresa}>Conectar novo número</Button>}
      />

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 24px 8px' }}>
        {recurso.erro ? (
          <section style={{ ...card }}><Aviso titulo="Os números não carregaram" texto={recurso.erro.mensagem} acao={recurso.erro.temporario ? 'Tentar de novo' : null} aoAcao={recurso.recarregar} /></section>
        ) : !recurso.carregando && numeros.length === 0 ? (
          <section style={{ ...card }}><Aviso titulo="Nenhum número conectado ainda" texto="Conecte o primeiro para a Sofia começar a atender." acao="Conectar novo número" aoAcao={() => definirConexao({ novo: true })} /></section>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
              {numeros.map((n) => (
                <Celular key={n.numero_id} numero={n} aoParear={() => definirConexao({ numero: n })} aoMudar={recurso.recarregar} />
              ))}
            </div>
            <Nota>Cada número atende de forma independente. Depois de conectado, ligue-o a uma campanha na aba Campanhas. Desconectar encerra a sessão e mantém o cadastro para reconectar; remover apaga o celular da plataforma (as conversas ficam).</Nota>
          </>
        )}
      </div>

      {conexao && <Conectar empresa={empresa} numero={conexao.numero || null} aoFechar={fechar} />}
    </div>
  );
}

/** Um celular da lista, com as ações de quem o administra.
 *
 *  Duas ações, com pesos diferentes — e por isso tratadas diferente:
 *   - **Desconectar** encerra a sessão do WhatsApp e mantém o cadastro: o card
 *     fica "Desconectado" com o botão de reconectar. Reversível com um QR.
 *   - **Remover** apaga a instância na Evolution e o cadastro daqui. Pede
 *     confirmação NO CARD (não um `confirm()` do navegador, que some no meio
 *     de um clique apressado): o footer vira a pergunta, com o que acontece
 *     escrito. As conversas ficam — são chaveadas pelo JID, não pelo cadastro. */
function Celular({ numero: n, aoParear, aoMudar }) {
  const st = estado(n.conexao);
  const semCelular = !n.telefone && !n.sender;
  const [confirmando, definirConfirmando] = useState(false);
  const [ocupado, definirOcupado] = useState(null);   // 'desconectando' | 'removendo'
  const [erro, definirErro] = useState(null);

  async function desconectar() {
    definirOcupado('desconectando');
    definirErro(null);
    try {
      await api.desconectarInstancia(n.numero_id);
      aoMudar();
    } catch (e) {
      definirErro(e?.mensagem || 'Não foi possível desconectar.');
    } finally {
      definirOcupado(null);
    }
  }

  async function remover() {
    definirOcupado('removendo');
    definirErro(null);
    try {
      await api.removerNumero(n.numero_id);
      aoMudar();
    } catch (e) {
      definirErro(e?.mensagem || 'Não foi possível remover.');
      definirOcupado(null);
      definirConfirmando(false);
    }
  }

  return (
    <div style={{ ...card, padding: 18, gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 'var(--radius-md)', background: st.ok ? 'var(--success-soft)' : 'var(--surface-sunken)', color: st.ok ? 'var(--success-text)' : 'var(--text-muted)' }}>
            <Icon name="smartphone" size={20} />
          </span>
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ font: 'var(--type-title)' }}>{semCelular ? n.instancia : telefoneLegivel(n.telefone || n.sender)}</span>
            <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>
              {semCelular ? 'ainda sem celular pareado' : n.verificado_em ? `verificado ${relativo(n.verificado_em)}` : n.instancia}
            </span>
          </span>
        </span>
        <Tag tone={st.tone} dot>{st.rotulo}</Tag>
      </div>

      {!st.ok && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 12px', background: 'var(--warning-soft)', borderRadius: 'var(--radius-control)' }}>
          <span style={{ font: 'var(--type-caption)', color: 'var(--warning-text)' }}>{semCelular ? 'Leia o QR para parear um celular.' : 'Este número precisa ser reconectado.'}</span>
          <Button variant="secondary" icon={semCelular ? 'qr-code' : 'rotate-cw'} style={{ ...botaoPequeno, fontSize: 'var(--fs-body-sm)' }} onClick={aoParear} disabled={Boolean(ocupado)}>{semCelular ? 'Parear' : 'Reconectar'}</Button>
        </div>
      )}

      {erro && <span style={{ font: 'var(--type-caption)', color: 'var(--danger-text, var(--danger))' }}>{erro}</span>}

      {confirmando ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 12px', background: 'var(--danger-soft)', borderRadius: 'var(--radius-control)' }}>
          <span style={{ font: 'var(--type-caption)', color: 'var(--danger-text)' }}>
            Remover este celular? Ele para de atender e sai da plataforma. As conversas já registradas ficam.
          </span>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" style={botaoPequeno} onClick={() => definirConfirmando(false)} disabled={ocupado === 'removendo'}>Cancelar</Button>
            <Button variant="secondary" icon="circle-slash" style={{ ...botaoPequeno, color: 'var(--danger-text)', borderColor: 'var(--danger)' }} onClick={remover} disabled={ocupado === 'removendo'}>{ocupado === 'removendo' ? 'Removendo…' : 'Remover'}</Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
          {st.ok && (
            <Button variant="ghost" icon="log-out" style={botaoPequeno} onClick={desconectar} disabled={Boolean(ocupado)} title="Encerra a sessão do WhatsApp; o cadastro fica para reconectar">
              {ocupado === 'desconectando' ? 'Desconectando…' : 'Desconectar'}
            </Button>
          )}
          <Button variant="ghost" icon="circle-slash" style={{ ...botaoPequeno, color: 'var(--danger-text)' }} onClick={() => definirConfirmando(true)} disabled={Boolean(ocupado)} title="Apaga o celular da plataforma">
            Remover
          </Button>
        </div>
      )}
    </div>
  );
}

/** Fluxo real de conexão por QR.
 *
 *  Fases: `preparando` (cadastro + instância) → `qr` (lendo; sonda a cada
 *  2,5 s) → `conectado` (com o telefone confirmado) | `erro`.
 *
 *  Para um número NOVO, cria o cadastro e a instância. Para um número que já
 *  existe (parear/reconectar), só pede um QR para a instância que já está lá —
 *  criar de novo seria recusado pela Evolution. */
function Conectar({ empresa, numero, aoFechar }) {
  const [fase, definirFase] = useState('preparando');
  const [qr, definirQr] = useState(null);
  const [alvo, definirAlvo] = useState(numero);   // o número que está sendo pareado
  const [dono, definirDono] = useState(null);
  const [conflito, definirConflito] = useState(null);
  const [erro, definirErro] = useState(null);
  const [gerando, definirGerando] = useState(false);
  const mudou = useRef(false);
  const iniciado = useRef(false);

  // O QR é ação; o estado é consulta. Só o segundo vai em laço.
  //
  // Este efeito CRIA coisas (cadastro + instância), então precisa rodar UMA
  // vez por modal. O StrictMode monta cada efeito duas vezes em dev de
  // propósito, e sem a guarda ele criou dois números e duas instâncias na
  // Evolution — o segundo apareceria na lista como "sem celular pareado" sem
  // ninguém ter pedido. Guarda por ref, e não por cleanup: o cleanup também
  // roda no remount falso, e desarmaria o fluxo em andamento.
  useEffect(() => {
    if (iniciado.current) return;
    iniciado.current = true;

    async function preparar() {
      try {
        let n = numero;
        // Variável local, não o estado `qr`: dentro deste efeito o estado é o
        // do mount (null), e testá-lo pediria um SEGUNDO QR logo depois da
        // criação — o que reinicia a negociação com o WhatsApp.
        let codigo = null;
        if (!n) {
          const criado = await api.criarNumero({ sender: empresa });
          n = { numero_id: criado.numero_id, instancia: criado.instancia };
          mudou.current = true;
          definirAlvo(n);
          const inst = await api.criarInstancia(n.numero_id);
          codigo = inst.qrcode || null;
        }
        if (!codigo) {
          // Número existente, ou a criação não trouxe QR: pede um agora.
          const atual = await api.qrcode(n.numero_id);
          if (atual.conectada) { concluir(atual); return; }
          codigo = atual.qrcode || null;
        }
        definirQr(codigo);
        definirFase('qr');
      } catch (e) {
        definirErro(e?.mensagem || e?.message || 'Não foi possível preparar a conexão.');
        definirFase('erro');
      }
    }

    preparar();
    // Fechar o modal o desmonta de verdade; um setState que chegue depois é
    // ignorado pelo React 18 sem aviso — não há o que desarmar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fase !== 'qr' || !alvo) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await api.estadoDaInstancia(alvo.numero_id);
        if (r.conectada) concluir(r);
      } catch { /* a próxima volta tenta de novo */ }
    }, INTERVALO_DE_SONDAGEM_MS);
    return () => clearInterval(id);
  }, [fase, alvo]);

  function concluir(resposta) {
    mudou.current = true;
    definirDono(resposta.dono || null);
    definirConflito(resposta.conflito || null);
    definirFase('conectado');
  }

  async function novoCodigo() {
    if (!alvo) return;
    definirGerando(true);
    try {
      const atual = await api.qrcode(alvo.numero_id);
      if (atual.conectada) { concluir(atual); return; }
      definirQr(atual.qrcode || null);
    } catch (e) {
      definirErro(e?.mensagem || 'Não foi possível gerar um código novo.');
    } finally {
      definirGerando(false);
    }
  }

  const fechar = () => aoFechar(mudou.current);

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'var(--overlay-scrim, rgba(4,5,20,.5))', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 5 }}>
      <div style={{ ...card, width: 440, maxWidth: '100%', boxShadow: 'var(--shadow-md)' }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
          <h3 style={{ margin: 0, font: 'var(--type-h3)' }}>{numero ? 'Parear o celular' : 'Conectar novo número'}</h3>
          <button type="button" onClick={fechar} aria-label="Fechar" style={{ display: 'inline-flex', width: 32, height: 32, alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 0, color: 'var(--text-secondary)', cursor: 'pointer' }}><Icon name="x" size={18} /></button>
        </header>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          {fase === 'preparando' && (
            <>
              <div style={{ width: 180, height: 180, borderRadius: 'var(--radius-card)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-placeholder)' }}>
                <Icon name="qr-code" size={64} />
              </div>
              <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>Preparando a conexão…</span>
            </>
          )}

          {fase === 'qr' && (
            <>
              {qr ? (
                <img src={qr} alt="QR code para parear o WhatsApp" width={180} height={180} style={{ borderRadius: 'var(--radius-card)', border: '1px solid var(--border-default)', background: '#fff' }} />
              ) : (
                <div style={{ width: 180, height: 180, borderRadius: 'var(--radius-card)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-placeholder)' }}>
                  <Icon name="qr-code" size={64} />
                </div>
              )}
              <ol style={{ margin: 0, paddingLeft: 20, font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)', lineHeight: 'var(--lh-relaxed)', alignSelf: 'stretch' }}>
                <li>Abra o WhatsApp no celular que vai atender.</li>
                <li>Toque em Dispositivos conectados › Conectar dispositivo.</li>
                <li>Aponte a câmera para este código.</li>
              </ol>
              <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', textAlign: 'center' }}>
                Aguardando a leitura no aparelho… O código expira em poucos segundos; se não ler a tempo, gere outro.
              </span>
              {erro && <span style={{ font: 'var(--type-caption)', color: 'var(--danger-text, var(--danger))' }}>{erro}</span>}
            </>
          )}

          {fase === 'conectado' && (
            <>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 64, height: 64, borderRadius: '50%', background: 'var(--success-soft)', color: 'var(--success-text)' }}>
                <Icon name="check" size={32} />
              </span>
              <span style={{ font: 'var(--type-title)' }}>Celular conectado</span>
              {dono?.telefone ? (
                <span style={{ font: 'var(--type-body)', color: 'var(--text-secondary)' }}>
                  {telefoneLegivel(dono.telefone)}{dono.nome_do_perfil ? ` · ${dono.nome_do_perfil}` : ''}
                </span>
              ) : (
                <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', textAlign: 'center' }}>Confirmando qual celular entrou… aparece na lista em instantes.</span>
              )}
              {conflito && (
                <div style={{ padding: '10px 12px', background: 'var(--warning-soft)', borderRadius: 'var(--radius-control)', font: 'var(--type-caption)', color: 'var(--warning-text)' }}>{conflito}</div>
              )}
              {!conflito && dono?.telefone && (
                <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)', textAlign: 'center' }}>Agora ligue este número a uma campanha na aba Campanhas.</span>
              )}
            </>
          )}

          {fase === 'erro' && (
            <Aviso titulo="Não deu para conectar" texto={erro} />
          )}
        </div>

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
          {fase === 'qr' && <Button variant="secondary" icon="rotate-cw" onClick={novoCodigo} disabled={gerando}>{gerando ? 'Gerando…' : 'Gerar novo código'}</Button>}
          <Button variant={fase === 'conectado' ? 'primary' : 'ghost'} onClick={fechar}>{fase === 'conectado' ? 'Concluir' : 'Fechar'}</Button>
        </footer>
      </div>
    </div>
  );
}
